import i18n from '../i18n/config';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n/config';

type DateLike = Date | string | number | null | undefined;
const UTC_NAIVE_DATETIME_PATTERN =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?)$/;

class LanguageService {
  private static instance: LanguageService;
  private readonly STORAGE_KEY = 'i18nextLng';

  public static getInstance(): LanguageService {
    if (!LanguageService.instance) {
      LanguageService.instance = new LanguageService();
    }
    return LanguageService.instance;
  }

  /**
   * Change language with immediate UI updates and persistence
   */
  public async changeLanguage(languageCode: SupportedLanguage): Promise<void> {
    try {
      // Store in localStorage FIRST to ensure it's saved before i18n changes
      localStorage.setItem(this.STORAGE_KEY, languageCode);
      
      // Change the i18n language
      await i18n.changeLanguage(languageCode);
      
      // Update document language
      document.documentElement.lang = languageCode;
      
      // Log for debugging
      console.log(`Language changed to: ${languageCode}, saved to localStorage`);
      
    } catch (error) {
      console.error('Failed to change language:', error);
      throw error;
    }
  }

  /**
   * Get current language
   */
  public getCurrentLanguage(): SupportedLanguage {
    return (i18n.language as SupportedLanguage) || 'zh-CN';
  }

  /**
   * Get all supported languages
   */
  public getSupportedLanguages(): typeof SUPPORTED_LANGUAGES {
    return SUPPORTED_LANGUAGES;
  }

  /**
   * Detect browser language
   */
  public detectBrowserLanguage(): SupportedLanguage {
    const browserLang = navigator.language || navigator.languages?.[0] || 'zh-CN';
    
    // Check if browser language is supported
    if (browserLang.startsWith('zh-CN') || browserLang.startsWith('zh-Hans')) {
      return 'zh-CN';
    }
    if (browserLang.startsWith('zh-HK') || browserLang.startsWith('zh-Hant')) {
      return 'zh-HK';
    }
    if (browserLang.startsWith('en')) {
      return 'en';
    }
    
    // Default fallback
    return 'zh-CN';
  }

  /**
   * Check if language is supported
   */
  public isLanguageSupported(languageCode: string): languageCode is SupportedLanguage {
    return languageCode in SUPPORTED_LANGUAGES;
  }
}

// Export singleton instance
export const languageService = LanguageService.getInstance();

const getCurrentLocale = (): string => {
  const currentLang = languageService.getCurrentLanguage();
  return currentLang === 'en' ? 'en-US' : currentLang;
};

const normalizeDateInput = (value: DateLike): Date | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const utcNaiveMatch = trimmed.match(UTC_NAIVE_DATETIME_PATTERN);
    if (utcNaiveMatch) {
      const [, datePart, timePart] = utcNaiveMatch;
      const normalizedTimePart = timePart.replace(/\.(\d{1,6})$/, (_, fraction: string) => {
        return `.${fraction.slice(0, 3).padEnd(3, '0')}`;
      });
      const date = new Date(`${datePart}T${normalizedTimePart}Z`);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const padDatePart = (value: number): string => String(value).padStart(2, '0');

const formatSlashDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hour = padDatePart(date.getHours());
  const minute = padDatePart(date.getMinutes());
  const second = padDatePart(date.getSeconds());

  return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
};

// Export locale service for formatting
export const localeService = {
  parseDate: (value: DateLike): Date | null => {
    return normalizeDateInput(value);
  },

  formatDate: (value: DateLike, format?: string | Intl.DateTimeFormatOptions): string => {
    const date = normalizeDateInput(value);
    if (!date) {
      return '-';
    }

    if (typeof format === 'string') {
      return new Intl.DateTimeFormat(getCurrentLocale()).format(date);
    }

    if (!format) {
      const year = date.getFullYear();
      const month = padDatePart(date.getMonth() + 1);
      const day = padDatePart(date.getDate());
      const hour = padDatePart(date.getHours());
      const minute = padDatePart(date.getMinutes());

      return `${year}/${month}/${day} ${hour}:${minute}`;
    }

    return new Intl.DateTimeFormat(getCurrentLocale(), {
      ...format,
      hour12: format.hour ? false : format.hour12,
      hourCycle: format.hour ? 'h23' : format.hourCycle,
    }).format(date);
  },

  formatDateTime: (value: DateLike): string => {
    const date = normalizeDateInput(value);
    if (!date) {
      return '-';
    }

    return formatSlashDateTime(date);
  },

  formatDateOnly: (value: DateLike, options?: Intl.DateTimeFormatOptions): string => {
    const date = normalizeDateInput(value);
    if (!date) {
      return '-';
    }

    return new Intl.DateTimeFormat(getCurrentLocale(), options ?? {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  },

  formatTime: (value: DateLike, options?: Intl.DateTimeFormatOptions): string => {
    const date = normalizeDateInput(value);
    if (!date) {
      return '-';
    }

    return new Intl.DateTimeFormat(getCurrentLocale(), options ?? {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      hourCycle: 'h23',
    }).format(date);
  },

  formatNumber: (number: number, options?: Intl.NumberFormatOptions): string => {
    return new Intl.NumberFormat(getCurrentLocale(), options).format(number);
  },

  formatFileSize: (bytes: number): string => {
    const currentLanguage = languageService.getCurrentLanguage();
    const byteDefault = currentLanguage === 'zh-HK' ? '位元組' : '字节';
    const units = [
      i18n.t('common:units.bytes', { lng: currentLanguage, defaultValue: byteDefault }),
      i18n.t('common:units.kb', { lng: currentLanguage, defaultValue: 'KB' }),
      i18n.t('common:units.mb', { lng: currentLanguage, defaultValue: 'MB' }),
      i18n.t('common:units.gb', { lng: currentLanguage, defaultValue: 'GB' }),
      i18n.t('common:units.tb', { lng: currentLanguage, defaultValue: 'TB' }),
    ];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    const formattedSize = unitIndex === 0 ? String(Math.round(size)) : size.toFixed(1);
    return `${formattedSize} ${units[unitIndex]}`;
  },

  formatCurrency: (amount: number, currency = 'CNY'): string => {
    return new Intl.NumberFormat(getCurrentLocale(), {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  },
};
